import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Bell, 
  ExternalLink, 
  DollarSign, 
  X, 
  BarChart3, 
  Briefcase, 
  Award, 
  TrendingUp, 
  Settings,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Users,
  Search,
  Eye,
  Edit,
  Trash2,
  Plus,
  Upload,
  FileText,
  Download
} from 'lucide-react';

import { getVacancies, getExamResults, getExamNotifications, updateVacancy, updateExamResult, updateExamNotification, deleteVacancy, deleteExamResult, deleteExamNotification, subscribeToVacancies, subscribeToExamResults, subscribeToExamNotifications, saveVacancy, saveExamResult, saveExamNotification } from '../../firebase/collections';
import FileUpload, { useFileUpload } from '../../components/FileUpload';

// Constants for vacancy management
const VACANCY_CATEGORIES = ['Civil Services', 'Government Jobs', 'Banking', 'Teaching', 'Engineering', 'Medical', 'IT', 'Other'];
const ORGANIZATIONS = ['Union Public Service Commission', 'Staff Selection Commission', 'Banking Services Board', 'State PSCs'];
const VACANCY_STATUSES = [
  { value: 'active', label: 'Active', color: 'bg-green-100 text-green-800' },
  { value: 'inactive', label: 'Inactive', color: 'bg-gray-100 text-gray-800' },
  { value: 'archived', label: 'Archived', color: 'bg-yellow-100 text-yellow-800' }
];
const EXAM_RESULT_STATUSES = [
  { value: 'published', label: 'Published', color: 'bg-green-100 text-green-800' },
  { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'archived', label: 'Archived', color: 'bg-gray-100 text-gray-800' }
];
const NOTIFICATION_TYPES = [
  { value: 'schedule', label: 'Schedule', color: 'bg-blue-100 text-blue-800' },
  { value: 'result', label: 'Result', color: 'bg-green-100 text-green-800' },
  { value: 'admit_card', label: 'Admit Card', color: 'bg-purple-100 text-purple-800' },
  { value: 'general', label: 'General', color: 'bg-gray-100 text-gray-800' }
];

// Helper functions
const getVacancyStats = (vacancies) => {
  return {
    total: vacancies.length,
    active: vacancies.filter(v => v.status === 'active').length,
    inactive: vacancies.filter(v => v.status === 'inactive').length,
    archived: vacancies.filter(v => v.status === 'archived').length
  };
};

const getCategoryStats = (items) => {
  const stats = {};
  items.forEach(item => {
    const category = item.category || item.type || 'Other';
    stats[category] = (stats[category] || 0) + 1;
  });
  return stats;
};

// Utility functions
const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const getStatusColor = (status, type = 'vacancy') => {
  let statusConfig;
  switch (type) {
    case 'vacancy':
      statusConfig = VACANCY_STATUSES.find(s => s.value === status);
      break;
    case 'result':
      statusConfig = EXAM_RESULT_STATUSES.find(s => s.value === status);
      break;
    case 'notification':
      statusConfig = VACANCY_STATUSES.find(s => s.value === status);
      break;
    default:
      statusConfig = VACANCY_STATUSES.find(s => s.value === status);
  }
  return statusConfig ? (statusConfig.color || 'bg-gray-100 text-gray-800') : 'bg-gray-100 text-gray-800';
};

const getTypeColor = (type) => {
  const typeConfig = NOTIFICATION_TYPES.find(t => t.value === type);
  return typeConfig ? typeConfig.color : 'bg-gray-100 text-gray-800';
};

const VacancyManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
    organization: 'all',
    type: 'all'
  });
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedItems, setSelectedItems] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Data states
  const [vacancies, setVacancies] = useState([]);
  const [examResults, setExamResults] = useState([]);
  const [examNotifications, setExamNotifications] = useState([]);
  const [vacancyStats, setVacancyStats] = useState({});
  const [categoryStats, setCategoryStats] = useState({});

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [modalType, setModalType] = useState('vacancy'); // vacancy, result, notification

  // Tab configuration
  const tabs = [
    { id: 'overview', name: 'Overview', icon: BarChart3 },
    { id: 'vacancies', name: 'Vacancies', icon: Briefcase },
    { id: 'results', name: 'Exam Results', icon: Award },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'analytics', name: 'Analytics', icon: TrendingUp },
    { id: 'settings', name: 'Settings', icon: Settings }
  ];

  useEffect(() => {
    loadAllData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Subscribe to real-time updates
  useEffect(() => {
    // Subscribe to vacancies
    const unsubscribeVacancies = subscribeToVacancies((result) => {
      if (result.success && Array.isArray(result.data)) {
        setVacancies(result.data);
        setVacancyStats(getVacancyStats(result.data));
      } else {
        }
    });

    // Subscribe to exam results
    const unsubscribeResults = subscribeToExamResults((result) => {
      if (result.success && Array.isArray(result.data)) {
        setExamResults(result.data);
      } else {
        }
    });

    // Subscribe to exam notifications
    const unsubscribeNotifications = subscribeToExamNotifications((result) => {
      if (result.success && Array.isArray(result.data)) {
        setExamNotifications(result.data);
      } else {
        }
    });

    // Cleanup function
    return () => {
      unsubscribeVacancies();
      unsubscribeResults();
      unsubscribeNotifications();
    };
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      setIsLoading(true);
      setError(null);

      // Load vacancies from Firebase
      const vacanciesResult = await getVacancies();
      if (vacanciesResult.success) {
        setVacancies(vacanciesResult.data);
        setVacancyStats(getVacancyStats(vacanciesResult.data));
      } else {
        setVacancies([]);
        setVacancyStats(getVacancyStats([]));
      }

      // Load exam results from Firebase
      const resultsResult = await getExamResults();
      if (resultsResult.success) {
        setExamResults(resultsResult.data);
      } else {
        setExamResults([]);
      }

      // Load exam notifications from Firebase
      const notificationsResult = await getExamNotifications();
      if (notificationsResult.success) {
        setExamNotifications(notificationsResult.data);
      } else {
        setExamNotifications([]);
      }

      // Calculate category stats
      const allItems = [
        ...(vacanciesResult.success ? vacanciesResult.data : []),
        ...(resultsResult.success ? resultsResult.data : []),
        ...(notificationsResult.success ? notificationsResult.data : [])
      ];
      setCategoryStats(getCategoryStats(allItems));

    } catch (error) {
      setError('Failed to load data. Please try again.');
      // Set empty arrays on error
      setVacancies([]);
      setExamResults([]);
      setExamNotifications([]);
      setVacancyStats(getVacancyStats([]));
      setCategoryStats(getCategoryStats([]));
    } finally {
      setLoading(false);
      setIsLoading(false);
    }
  };

  const handleAddNew = (type) => {
    setModalType(type);
    setSelectedItem(null);
    setShowAddModal(true);
  };

  const handleEdit = (item, type) => {
    setSelectedItem(item);
    setModalType(type);
    setShowEditModal(true);
  };

  const handleDelete = (item, type) => {
    setItemToDelete({ item, type });
    setShowDeleteModal(true);
  };

  const handleViewDetails = (item, type) => {
    setSelectedItem(item);
    setModalType(type);
    setShowDetailsModal(true);
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedItems.length === 0) return;

    try {
      setLoading(true);
      let updates = {};

      switch (bulkAction) {
        case 'activate':
          updates = { status: 'active' };
          break;
        case 'deactivate':
          updates = { status: 'inactive' };
          break;
        case 'archive':
          updates = { status: 'archived' };
          break;
        case 'delete':
          // Handle bulk delete
          const deletePromises = selectedItems.map(({ id, type }) => {
            switch (type) {
              case 'vacancy':
                return deleteVacancyData(id);
              case 'result':
                return deleteExamResultData(id);
              case 'notification':
                return deleteExamNotificationData(id);
              default:
                return Promise.resolve({ success: false });
            }
          });
          await Promise.all(deletePromises);
          setSuccess(`Successfully deleted ${selectedItems.length} items`);
          setSelectedItems([]);
          setShowBulkActions(false);
          loadAllData();
          return;
        default:
          return;
      }

      // Handle bulk updates
      const updatePromises = selectedItems.map(({ id, type }) => {
        switch (type) {
          case 'vacancy':
            return updateVacancyData(id, updates);
          case 'result':
            return updateExamResultData(id, updates);
          case 'notification':
            return updateExamNotificationData(id, updates);
          default:
            return Promise.resolve({ success: false });
        }
      });

      const results = await Promise.all(updatePromises);
      const successCount = results.filter(r => r.success).length;
      const failureCount = results.length - successCount;

      setSuccess(`Successfully updated ${successCount} items${failureCount > 0 ? `, ${failureCount} failed` : ''}`);
      setSelectedItems([]);
      setShowBulkActions(false);
      loadAllData();
    } catch (error) {
      setError('Failed to perform bulk action');
    } finally {
      setIsLoading(false);
    }
  };

  // Missing functions (inline implementation)
  const deleteVacancyData = async (id) => {
    try {
      const result = await deleteVacancy(id);
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const deleteExamResultData = async (id) => {
    try {
      const result = await deleteExamResult(id);
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const deleteExamNotificationData = async (id) => {
    try {
      const result = await deleteExamNotification(id);
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateVacancyData = async (id, updates) => {
    try {
      const result = await updateVacancy(id, updates);
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateExamResultData = async (id, updates) => {
    try {
      const result = await updateExamResult(id, updates);
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateExamNotificationData = async (id, updates) => {
    try {
      const result = await updateExamNotification(id, updates);
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  // Filter vacancies based on search term and filters
  const filteredVacancies = vacancies.filter(vacancy => {
    const matchesSearch = !searchTerm || 
      vacancy.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vacancy.organization?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vacancy.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vacancy.category?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filters.status === 'all' || vacancy.status === filters.status;
    const matchesCategory = filters.category === 'all' || vacancy.category === filters.category;
    const matchesOrganization = filters.organization === 'all' || vacancy.organization === filters.organization;
    
    return matchesSearch && matchesStatus && matchesCategory && matchesOrganization;
  });

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white p-6 rounded-lg shadow">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Vacancy Management</h1>
            <p className="text-gray-600 dark:text-gray-400">
              Manage job vacancies, exam results, and notifications
              <span className="ml-2 text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                {vacancies.length} vacancies, {examResults.length} results, {examNotifications.length} notifications
              </span>
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={loadAllData}
              className="btn-secondary inline-flex items-center"
              title="Refresh data"
            >
              <RefreshCw size={16} className="mr-2" />
              Refresh
            </button>
            <div className="relative">
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    handleAddNew(e.target.value);
                  }
                }}
                className="btn-primary inline-flex items-center"
                defaultValue=""
              >
                <option value="" disabled>Add New</option>
                <option value="vacancy">Job Vacancy</option>
                <option value="result">Exam Result</option>
                <option value="notification">Notification</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
            <span className="text-red-800">{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-400 mr-2" />
            <span className="text-green-800">{success}</span>
            <button
              onClick={() => setSuccess(null)}
              className="ml-auto text-green-400 hover:text-green-600"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <tab.icon size={16} />
              <span>{tab.name}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
              {activeTab === 'overview' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Briefcase className="text-blue-600" size={24} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Vacancies</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{vacancyStats.total}</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Award className="text-green-600" size={24} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Exam Results</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{examResults.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Bell className="text-purple-600" size={24} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Notifications</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{examNotifications.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-3 bg-yellow-100 rounded-lg">
                  <Users className="text-yellow-600" size={24} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Posts</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{vacancyStats.totalPosts}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Vacancy Status Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Vacancy Status</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Active</span>
                  <span className="text-sm font-medium text-green-600">{vacancyStats.active}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Inactive</span>
                  <span className="text-sm font-medium text-red-600">{vacancyStats.inactive}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Expired</span>
                  <span className="text-sm font-medium text-gray-600">{vacancyStats.expired}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Draft</span>
                  <span className="text-sm font-medium text-yellow-600">{vacancyStats.draft}</span>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top Categories</h3>
              <div className="space-y-3">
                {Object.entries(categoryStats)
                  .sort(([,a], [,b]) => b - a)
                  .slice(0, 5)
                  .map(([category, count]) => (
                    <div key={category} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{category}</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {count} items
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Activity</h3>
            <div className="space-y-4">
              {[...vacancies, ...examResults, ...examNotifications]
                .sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt))
                .slice(0, 5)
                .map((item, index) => (
                  <div key={`${item.id}-${index}`} className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-4"></div>
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {item.title || item.examName} - {item.status}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {formatDate(item.updatedAt || item.createdAt)}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </motion.div>
      )}

              {activeTab === 'vacancies' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >


          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search vacancies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              >
                <option value="all">All Status</option>
                {VACANCY_STATUSES.map(status => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
              <select
                value={filters.category}
                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              >
                <option value="all">All Categories</option>
                {VACANCY_CATEGORIES.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              <select
                value={filters.organization}
                onChange={(e) => setFilters(prev => ({ ...prev, organization: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              >
                <option value="all">All Organizations</option>
                {ORGANIZATIONS.map(org => (
                  <option key={org} value={org}>{org}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Bulk Actions */}
          {selectedItems.length > 0 && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-blue-800">
                  {selectedItems.length} vacancy(s) selected
                </span>
                <div className="flex items-center space-x-2">
                  <select
                    value={bulkAction}
                    onChange={(e) => setBulkAction(e.target.value)}
                    className="px-3 py-1 border border-blue-300 rounded text-sm"
                  >
                    <option value="">Select Action</option>
                    <option value="activate">Activate</option>
                    <option value="deactivate">Deactivate</option>
                    <option value="archive">Archive</option>
                    <option value="delete">Delete</option>
                  </select>
                  <button
                    onClick={handleBulkAction}
                    disabled={!bulkAction}
                    className="btn-primary text-sm px-3 py-1 disabled:opacity-50"
                  >
                    Apply
                  </button>
                  <button
                    onClick={() => {
                      setSelectedItems([]);
                      setBulkAction('');
                    }}
                    className="btn-secondary text-sm px-3 py-1"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Vacancies Table */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedItems.length === filteredVacancies.length && filteredVacancies.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedItems(filteredVacancies.map(v => ({ id: v.id, type: 'vacancy' })));
                          } else {
                            setSelectedItems([]);
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Vacancy
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Organization
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Posts
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Last Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredVacancies.map((vacancy) => (
                    <tr key={vacancy.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedItems.some(item => item.id === vacancy.id && item.type === 'vacancy')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedItems(prev => [...prev, { id: vacancy.id, type: 'vacancy' }]);
                            } else {
                              setSelectedItems(prev => prev.filter(item => !(item.id === vacancy.id && item.type === 'vacancy')));
                            }
                          }}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {vacancy.title}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {vacancy.location}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {vacancy.organization}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900 dark:text-white">
                          {vacancy.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {vacancy.totalPosts}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatDate(vacancy.lastDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(vacancy.status, 'vacancy')}`}>
                          {vacancy.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleViewDetails(vacancy, 'vacancy')}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => handleEdit(vacancy, 'vacancy')}
                            className="text-green-600 hover:text-green-900"
                            title="Edit Vacancy"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(vacancy, 'vacancy')}
                            className="text-red-600 hover:text-red-900"
                            title="Delete Vacancy"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Empty State */}
          {filteredVacancies.length === 0 && (
            <div className="text-center py-12">
              <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No vacancies found
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Get started by creating your first job vacancy
              </p>
              <button
                onClick={() => handleAddNew('vacancy')}
                className="btn-primary inline-flex items-center"
              >
                <Plus size={16} className="mr-2" />
                Add Vacancy
              </button>
            </div>
          )}
        </motion.div>
      )}

              {activeTab === 'results' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search exam results..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              >
                <option value="all">All Status</option>
                {EXAM_RESULT_STATUSES.map(status => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
              <select
                value={filters.type}
                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              >
                <option value="all">All Types</option>
                {NOTIFICATION_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Bulk Actions */}
          {selectedItems.length > 0 && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-blue-800">
                  {selectedItems.length} result(s) selected
                </span>
                <div className="flex items-center space-x-2">
                  <select
                    value={bulkAction}
                    onChange={(e) => setBulkAction(e.target.value)}
                    className="px-3 py-1 border border-blue-300 rounded text-sm"
                  >
                    <option value="">Select Action</option>
                    <option value="activate">Activate</option>
                    <option value="deactivate">Deactivate</option>
                    <option value="archive">Archive</option>
                    <option value="delete">Delete</option>
                  </select>
                  <button
                    onClick={handleBulkAction}
                    disabled={!bulkAction}
                    className="btn-primary text-sm px-3 py-1 disabled:opacity-50"
                  >
                    Apply
                  </button>
                  <button
                    onClick={() => {
                      setSelectedItems([]);
                      setBulkAction('');
                    }}
                    className="btn-secondary text-sm px-3 py-1"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Exam Results Table */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedItems.length === examResults.length && examResults.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedItems(examResults.map(r => ({ id: r.id, type: 'result' })));
                          } else {
                            setSelectedItems([]);
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Exam Result
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Organization
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Result Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {examResults.map((result) => (
                    <tr key={result.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedItems.some(item => item.id === result.id && item.type === 'result')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedItems(prev => [...prev, { id: result.id, type: 'result' }]);
                            } else {
                              setSelectedItems(prev => prev.filter(item => !(item.id === result.id && item.type === 'result')));
                            }
                          }}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {result.examName}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {result.examType}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {result.organization}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatDate(result.resultDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(result.status, 'result')}`}>
                          {result.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleViewDetails(result, 'result')}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => handleEdit(result, 'result')}
                            className="text-green-600 hover:text-green-900"
                            title="Edit Result"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(result, 'result')}
                            className="text-red-600 hover:text-red-900"
                            title="Delete Result"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Empty State */}
          {examResults.length === 0 && (
            <div className="text-center py-12">
              <Award className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No exam results found
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                No candidates have completed their exams yet.
              </p>
              <button
                onClick={() => handleAddNew('result')}
                className="btn-primary inline-flex items-center"
              >
                <Plus size={16} className="mr-2" />
                Add Result
              </button>
            </div>
          )}
        </motion.div>
      )}

      {activeTab === 'notifications' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search notifications..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={filters.type}
                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              >
                <option value="all">All Types</option>
                {NOTIFICATION_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Bulk Actions */}
          {selectedItems.length > 0 && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-blue-800">
                  {selectedItems.length} notification(s) selected
                </span>
                <div className="flex items-center space-x-2">
                  <select
                    value={bulkAction}
                    onChange={(e) => setBulkAction(e.target.value)}
                    className="px-3 py-1 border border-blue-300 rounded text-sm"
                  >
                    <option value="">Select Action</option>
                    <option value="activate">Activate</option>
                    <option value="deactivate">Deactivate</option>
                    <option value="archive">Archive</option>
                    <option value="delete">Delete</option>
                  </select>
                  <button
                    onClick={handleBulkAction}
                    disabled={!bulkAction}
                    className="btn-primary text-sm px-3 py-1 disabled:opacity-50"
                  >
                    Apply
                  </button>
                  <button
                    onClick={() => {
                      setSelectedItems([]);
                      setBulkAction('');
                    }}
                    className="btn-secondary text-sm px-3 py-1"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Exam Notifications Table */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedItems.length === examNotifications.length && examNotifications.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedItems(examNotifications.map(n => ({ id: n.id, type: 'notification' })));
                          } else {
                            setSelectedItems([]);
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Notification
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {examNotifications.map((notification) => (
                    <tr key={notification.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedItems.some(item => item.id === notification.id && item.type === 'notification')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedItems(prev => [...prev, { id: notification.id, type: 'notification' }]);
                            } else {
                              setSelectedItems(prev => prev.filter(item => !(item.id === notification.id && item.type === 'notification')));
                            }
                          }}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {notification.title}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {notification.message}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(notification.type)}`}>
                          {notification.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(notification.status, 'notification')}`}>
                          {notification.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleViewDetails(notification, 'notification')}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => handleEdit(notification, 'notification')}
                            className="text-green-600 hover:text-green-900"
                            title="Edit Notification"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(notification, 'notification')}
                            className="text-red-600 hover:text-red-900"
                            title="Delete Notification"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Empty State */}
          {examNotifications.length === 0 && (
            <div className="text-center py-12">
              <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No notifications found
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                No new notifications to display.
              </p>
              <button
                onClick={() => handleAddNew('notification')}
                className="btn-primary inline-flex items-center"
              >
                <Plus size={16} className="mr-2" />
                Add Notification
              </button>
            </div>
          )}
        </motion.div>
      )}

      {activeTab === 'settings' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Settings</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Manage application settings and configurations.
          </p>

          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Application Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Application Name
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="My Job Portal"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Default Currency
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="INR">Indian Rupee (₹)</option>
                  <option value="USD">US Dollar ($)</option>
                  <option value="EUR">Euro (€)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Default Language
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="en-IN">English (India)</option>
                  <option value="hi-IN">Hindi (India)</option>
                  <option value="bn-IN">Bengali (India)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Time Zone
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                  <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                  <option value="Asia/Singapore">Asia/Singapore (SGT)</option>
                </select>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow mt-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Email Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  SMTP Host
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="smtp.example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  SMTP Port
                </label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="587"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  SMTP Username
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="your_name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  SMTP Password
                </label>
                <input
                  type="password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="your_password"
                />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow mt-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Database Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Database Host
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="localhost"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Database Name
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="my_app_db"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Database Username
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="db_"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Database Password
                </label>
                <input
                  type="password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="db_password"
                />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow mt-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Advanced Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Cache Duration (minutes)
                </label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="60"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Session Duration (minutes)
                </label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="30"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Max File Upload Size (MB)
                </label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="10"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-6">
            <button className="btn-primary">Save Settings</button>
          </div>
        </motion.div>
      )}

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {showAddModal ? 'Add New' : 'Edit'} {modalType === 'vacancy' ? 'Vacancy' : modalType === 'result' ? 'Exam Result' : 'Notification'}
                </h2>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    setSelectedItem(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6">
                             {modalType === 'vacancy' && (
                 <VacancyForm
                   vacancy={selectedItem}
                   onSave={async (data) => {
                     try {
                       const result = await saveVacancy(data);
                       
                       if (result.success) {
                         setSuccess('Vacancy saved successfully');
                         setShowAddModal(false);
                         setShowEditModal(false);
                         loadAllData();
                       } else {
                         setError('Failed to save vacancy: ' + (result.error?.message || 'Unknown error'));
                       }
                     } catch (error) {
                       setError('Failed to save vacancy: ' + error.message);
                     }
                   }}
                   onCancel={() => {
                     setShowAddModal(false);
                     setShowEditModal(false);
                     setSelectedItem(null);
                   }}
                 />
               )}

              {modalType === 'result' && (
                <ExamResultForm
                  result={selectedItem}
                  onSave={async (data) => {
                    try {
                      const result = await saveExamResult(data);
                      
                      if (result.success) {
                        setSuccess('Exam result saved successfully');
                        setShowAddModal(false);
                        setShowEditModal(false);
                        loadAllData();
                      } else {
                        setError('Failed to save exam result: ' + (result.error?.message || 'Unknown error'));
                      }
                    } catch (error) {
                      setError('Failed to save exam result: ' + error.message);
                    }
                  }}
                  onCancel={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    setSelectedItem(null);
                  }}
                />
              )}

              {modalType === 'notification' && (
                <NotificationForm
                  notification={selectedItem}
                  onSave={async (data) => {
                    try {
                      const result = await saveExamNotification(data);
                      
                      if (result.success) {
                        setSuccess('Notification saved successfully');
                        setShowAddModal(false);
                        setShowEditModal(false);
                        loadAllData();
                      } else {
                        setError('Failed to save notification: ' + (result.error?.message || 'Unknown error'));
                      }
                    } catch (error) {
                      setError('Failed to save notification: ' + error.message);
                    }
                  }}
                  onCancel={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    setSelectedItem(null);
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <AlertCircle className="w-6 h-6 text-red-500 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Confirm Delete
                </h3>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Are you sure you want to delete this {itemToDelete?.type}? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      setLoading(true);
                      const { item, type } = itemToDelete;
                      let result;
                      
                      switch (type) {
                        case 'vacancy':
                          result = await deleteVacancy(item.id);
                          break;
                        case 'result':
                          result = await deleteExamResult(item.id);
                          break;
                        case 'notification':
                          result = await deleteExamNotification(item.id);
                          break;
                        default:
                          throw new Error('Unknown item type');
                      }
                      
                      if (result.success) {
                        setSuccess(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted successfully`);
                        setShowDeleteModal(false);
                        setItemToDelete(null);
                        loadAllData();
                      } else {
                        setError(`Failed to delete ${type}: ${result.error?.message || 'Unknown error'}`);
                      }
                    } catch (error) {
                      setError(`Failed to delete ${itemToDelete?.type}: ${error.message}`);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  className="btn-danger"
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {modalType === 'vacancy' ? 'Vacancy' : modalType === 'result' ? 'Exam Result' : 'Notification'} Details
                </h2>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedItem(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6">
              {modalType === 'vacancy' && selectedItem && (
                <VacancyDetails vacancy={selectedItem} />
              )}
              {modalType === 'result' && selectedItem && (
                <ExamResultDetails result={selectedItem} />
              )}
              {modalType === 'notification' && selectedItem && (
                <NotificationDetails notification={selectedItem} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Vacancy Form Component with File Upload
const VacancyForm = ({ vacancy, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: vacancy?.title || '',
    organization: vacancy?.organization || '',
    category: vacancy?.category || '',
    location: vacancy?.location || '',
    totalPosts: vacancy?.totalPosts || '',
    salary: vacancy?.salary || '',
    lastDate: vacancy?.lastDate || '',
    description: vacancy?.description || '',
    requirements: vacancy?.requirements || '',
    applicationLink: vacancy?.applicationLink || '',
    status: vacancy?.status || 'active',
    documents: vacancy?.documents || []
  });

  const [errors, setErrors] = useState({});
  const [showFileUpload, setShowFileUpload] = useState(false);

  const {
    uploadedFiles,
    uploading,
    progress,
    uploadFile,
    removeFile,
    setUploadedFiles
  } = useFileUpload({ storagePath: 'vacancy-documents' });

  // Handle file upload for vacancy documents
  const handleFilesUploaded = (files) => {
    setFormData(prev => ({
      ...prev,
      documents: [...(prev.documents || []), ...files]
    }));
  };

  const handleFileRemoved = (file) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.filter(f => f.name !== file.name)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Validate form data
    const newErrors = {};
    if (!formData.title) newErrors.title = 'Title is required';
    if (!formData.organization) newErrors.organization = 'Organization is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.lastDate) newErrors.lastDate = 'Last date is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Job Title *
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
              errors.title ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter job title"
          />
          {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Organization *
          </label>
          <select
            value={formData.organization}
            onChange={(e) => setFormData(prev => ({ ...prev, organization: e.target.value }))}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
              errors.organization ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select Organization</option>
            {ORGANIZATIONS.map(org => (
              <option key={org} value={org}>{org}</option>
            ))}
          </select>
          {errors.organization && <p className="text-red-500 text-sm mt-1">{errors.organization}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Category *
          </label>
          <select
            value={formData.category}
            onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
              errors.category ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select Category</option>
            {VACANCY_CATEGORIES.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
          {errors.category && <p className="text-red-500 text-sm mt-1">{errors.category}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Location
          </label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            placeholder="Enter location"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Total Posts
          </label>
          <input
            type="number"
            value={formData.totalPosts}
            onChange={(e) => setFormData(prev => ({ ...prev, totalPosts: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            placeholder="Number of posts"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Salary
          </label>
          <input
            type="text"
            value={formData.salary}
            onChange={(e) => setFormData(prev => ({ ...prev, salary: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            placeholder="Salary range"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Last Date to Apply *
          </label>
          <input
            type="date"
            value={formData.lastDate}
            onChange={(e) => setFormData(prev => ({ ...prev, lastDate: e.target.value }))}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
              errors.lastDate ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.lastDate && <p className="text-red-500 text-sm mt-1">{errors.lastDate}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Application Link
          </label>
          <input
            type="url"
            value={formData.applicationLink}
            onChange={(e) => setFormData(prev => ({ ...prev, applicationLink: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            placeholder="https://example.com/apply"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Status
          </label>
          <select
            value={formData.status}
            onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
                         {VACANCY_STATUSES.map(status => (
               <option key={status.value} value={status.value}>{status.label}</option>
             ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Job Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          placeholder="Enter job description"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Requirements
        </label>
        <textarea
          value={formData.requirements}
          onChange={(e) => setFormData(prev => ({ ...prev, requirements: e.target.value }))}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          placeholder="Enter job requirements"
        />
      </div>

      {/* File Upload Section */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Vacancy Documents</h3>
          <button
            type="button"
            onClick={() => setShowFileUpload(!showFileUpload)}
            className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Upload className="w-4 h-4 mr-2" />
            {showFileUpload ? 'Hide Upload' : 'Upload Documents'}
          </button>
        </div>

        {showFileUpload && (
          <div className="mb-4">
            <FileUpload
              onFilesUploaded={handleFilesUploaded}
              onFileRemoved={handleFileRemoved}
              multiple={true}
              maxFiles={10}
              maxSize={50 * 1024 * 1024} // 50MB
              storagePath="vacancy-documents"
              showPreview={true}
              showProgress={true}
              showActions={true}
              placeholder="Drag and drop vacancy documents here, or click to select"
              allowedTypes={[
                'image/*',
                'application/pdf',
                'text/*',
                'application/*',
                'video/*'
              ]}
            />
          </div>
        )}

        {/* Display uploaded documents */}
        {formData.documents && formData.documents.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Uploaded Documents:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {formData.documents.map((document, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <FileText className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{document.name}</p>
                      <p className="text-xs text-gray-500">{document.size}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleFileRemoved(document)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-primary"
        >
          {vacancy ? 'Update Vacancy' : 'Create Vacancy'}
        </button>
      </div>
    </form>
  );
};

// Placeholder components for other forms
const ExamResultForm = ({ result, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    examName: result?.examName || '',
    organization: result?.organization || '',
    examType: result?.examType || '',
    examDate: result?.examDate || '',
    resultDate: result?.resultDate || '',
    status: result?.status || 'published',
    resultLink: result?.resultLink || '',
    downloadLink: result?.downloadLink || '',
    description: result?.description || '',
    totalCandidates: result?.totalCandidates || '',
    documents: result?.documents || []
  });

  const [errors, setErrors] = useState({});
  const [showFileUpload, setShowFileUpload] = useState(false);

  const {
    uploadedFiles,
    uploading,
    progress,
    uploadFile,
    removeFile,
    setUploadedFiles
  } = useFileUpload({ storagePath: 'exam-result-documents' });

  // Handle file upload for exam result documents
  const handleFilesUploaded = (files) => {
    setFormData(prev => ({
      ...prev,
      documents: [...(prev.documents || []), ...files]
    }));
  };

  const handleFileRemoved = (file) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.filter(f => f.name !== file.name)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Validate form data
    const newErrors = {};
    if (!formData.examName) newErrors.examName = 'Exam name is required';
    if (!formData.organization) newErrors.organization = 'Organization is required';
    if (!formData.resultDate) newErrors.resultDate = 'Result date is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Exam Name *
          </label>
          <input
            type="text"
            value={formData.examName}
            onChange={(e) => setFormData(prev => ({ ...prev, examName: e.target.value }))}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
              errors.examName ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter exam name"
          />
          {errors.examName && <p className="text-red-500 text-sm mt-1">{errors.examName}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Organization *
          </label>
          <select
            value={formData.organization}
            onChange={(e) => setFormData(prev => ({ ...prev, organization: e.target.value }))}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
              errors.organization ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select Organization</option>
            {ORGANIZATIONS.map(org => (
              <option key={org} value={org}>{org}</option>
            ))}
          </select>
          {errors.organization && <p className="text-red-500 text-sm mt-1">{errors.organization}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Exam Type
          </label>
          <select
            value={formData.examType}
            onChange={(e) => setFormData(prev => ({ ...prev, examType: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="">Select Exam Type</option>
            <option value="Civil Services">Civil Services</option>
            <option value="Banking">Banking</option>
            <option value="Teaching">Teaching</option>
            <option value="Engineering">Engineering</option>
            <option value="Medical">Medical</option>
            <option value="IT">IT</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Exam Date
          </label>
          <input
            type="date"
            value={formData.examDate}
            onChange={(e) => setFormData(prev => ({ ...prev, examDate: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Total Candidates
          </label>
          <input
            type="number"
            value={formData.totalCandidates}
            onChange={(e) => setFormData(prev => ({ ...prev, totalCandidates: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            placeholder="Total number of candidates"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Result Date *
          </label>
          <input
            type="date"
            value={formData.resultDate}
            onChange={(e) => setFormData(prev => ({ ...prev, resultDate: e.target.value }))}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
              errors.resultDate ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.resultDate && <p className="text-red-500 text-sm mt-1">{errors.resultDate}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Status
          </label>
          <select
            value={formData.status}
            onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            {EXAM_RESULT_STATUSES.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Result Link
          </label>
          <input
            type="url"
            value={formData.resultLink}
            onChange={(e) => setFormData(prev => ({ ...prev, resultLink: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            placeholder="https://example.com/result"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Download Link
          </label>
          <input
            type="url"
            value={formData.downloadLink}
            onChange={(e) => setFormData(prev => ({ ...prev, downloadLink: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            placeholder="https://example.com/download"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          placeholder="Enter result description"
        />
      </div>

      {/* File Upload Section */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Result Documents</h3>
          <button
            type="button"
            onClick={() => setShowFileUpload(!showFileUpload)}
            className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Upload className="w-4 h-4 mr-2" />
            {showFileUpload ? 'Hide Upload' : 'Upload Documents'}
          </button>
        </div>

        {showFileUpload && (
          <div className="mb-4">
            <FileUpload
              onFilesUploaded={handleFilesUploaded}
              onFileRemoved={handleFileRemoved}
              multiple={true}
              maxFiles={10}
              maxSize={50 * 1024 * 1024} // 50MB
              storagePath="exam-result-documents"
              showPreview={true}
              showProgress={true}
              showActions={true}
              placeholder="Drag and drop result documents here, or click to select"
              allowedTypes={[
                'image/*',
                'application/pdf',
                'text/*',
                'application/*',
                'video/*'
              ]}
            />
          </div>
        )}

        {/* Display uploaded documents */}
        {formData.documents && formData.documents.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Uploaded Documents:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {formData.documents.map((document, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <FileText className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{document.name}</p>
                      <p className="text-xs text-gray-500">{document.size}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleFileRemoved(document)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-primary"
        >
          {result ? 'Update Result' : 'Create Result'}
        </button>
      </div>
    </form>
  );
};

const NotificationForm = ({ notification, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: notification?.title || '',
    message: notification?.message || '',
    type: notification?.type || 'general',
    organization: notification?.organization || '',
    examName: notification?.examName || '',
    notificationDate: notification?.notificationDate || '',
    link: notification?.link || '',
    status: notification?.status || 'active',
    priority: notification?.priority || 'normal',
    targetAudience: notification?.targetAudience || 'all',
    documents: notification?.documents || []
  });

  const [errors, setErrors] = useState({});
  const [showFileUpload, setShowFileUpload] = useState(false);

  const {
    uploadedFiles,
    uploading,
    progress,
    uploadFile,
    removeFile,
    setUploadedFiles
  } = useFileUpload({ storagePath: 'exam-notification-documents' });

  // Handle file upload for notification documents
  const handleFilesUploaded = (files) => {
    setFormData(prev => ({
      ...prev,
      documents: [...(prev.documents || []), ...files]
    }));
  };

  const handleFileRemoved = (file) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.filter(f => f.name !== file.name)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Validate form data
    const newErrors = {};
    if (!formData.title) newErrors.title = 'Title is required';
    if (!formData.message) newErrors.message = 'Message is required';
    if (!formData.type) newErrors.type = 'Type is required';
    if (!formData.notificationDate) newErrors.notificationDate = 'Notification date is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Title *
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
              errors.title ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter notification title"
          />
          {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Type *
          </label>
          <select
            value={formData.type}
            onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
              errors.type ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select Type</option>
            {NOTIFICATION_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
          {errors.type && <p className="text-red-500 text-sm mt-1">{errors.type}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Organization
          </label>
          <select
            value={formData.organization}
            onChange={(e) => setFormData(prev => ({ ...prev, organization: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="">Select Organization</option>
            {ORGANIZATIONS.map(org => (
              <option key={org} value={org}>{org}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Exam Name
          </label>
          <input
            type="text"
            value={formData.examName}
            onChange={(e) => setFormData(prev => ({ ...prev, examName: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            placeholder="Enter exam name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Notification Date *
          </label>
          <input
            type="date"
            value={formData.notificationDate}
            onChange={(e) => setFormData(prev => ({ ...prev, notificationDate: e.target.value }))}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
              errors.notificationDate ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.notificationDate && <p className="text-red-500 text-sm mt-1">{errors.notificationDate}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Priority
          </label>
          <select
            value={formData.priority}
            onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Target Audience
          </label>
          <select
            value={formData.targetAudience}
            onChange={(e) => setFormData(prev => ({ ...prev, targetAudience: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="all">All Users</option>
            <option value="students">Students</option>
            <option value="teachers">Teachers</option>
            <option value="admins">Administrators</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Status
          </label>
          <select
            value={formData.status}
            onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            {VACANCY_STATUSES.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Link
          </label>
          <input
            type="url"
            value={formData.link}
            onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            placeholder="https://example.com/notification"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Message *
        </label>
        <textarea
          value={formData.message}
          onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
          rows={4}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
            errors.message ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Enter notification message"
        />
        {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
      </div>

      {/* File Upload Section */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Notification Documents</h3>
          <button
            type="button"
            onClick={() => setShowFileUpload(!showFileUpload)}
            className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Upload className="w-4 h-4 mr-2" />
            {showFileUpload ? 'Hide Upload' : 'Upload Documents'}
          </button>
        </div>

        {showFileUpload && (
          <div className="mb-4">
            <FileUpload
              onFilesUploaded={handleFilesUploaded}
              onFileRemoved={handleFileRemoved}
              multiple={true}
              maxFiles={10}
              maxSize={50 * 1024 * 1024} // 50MB
              storagePath="exam-notification-documents"
              showPreview={true}
              showProgress={true}
              showActions={true}
              placeholder="Drag and drop notification documents here, or click to select"
              allowedTypes={[
                'image/*',
                'application/pdf',
                'text/*',
                'application/*',
                'video/*'
              ]}
            />
          </div>
        )}

        {/* Display uploaded documents */}
        {formData.documents && formData.documents.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Uploaded Documents:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {formData.documents.map((document, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <FileText className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{document.name}</p>
                      <p className="text-xs text-gray-500">{document.size}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleFileRemoved(document)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-primary"
        >
          {notification ? 'Update Notification' : 'Create Notification'}
        </button>
      </div>
    </form>
  );
};

// Placeholder components for details
const VacancyDetails = ({ vacancy }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Basic Information</h3>
        <div className="space-y-3">
          <div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Title:</span>
            <p className="text-gray-900 dark:text-white">{vacancy.title}</p>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Organization:</span>
            <p className="text-gray-900 dark:text-white">{vacancy.organization}</p>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Category:</span>
            <p className="text-gray-900 dark:text-white">{vacancy.category}</p>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Location:</span>
            <p className="text-gray-900 dark:text-white">{vacancy.location}</p>
          </div>
        </div>
      </div>
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Job Details</h3>
        <div className="space-y-3">
          <div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Posts:</span>
            <p className="text-gray-900 dark:text-white">{vacancy.totalPosts}</p>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Salary:</span>
            <p className="text-gray-900 dark:text-white">{vacancy.salary}</p>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Date:</span>
            <p className="text-gray-900 dark:text-white">{formatDate(vacancy.lastDate)}</p>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Status:</span>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(vacancy.status, 'vacancy')}`}>
              {vacancy.status}
            </span>
          </div>
        </div>
      </div>
    </div>

    {vacancy.description && (
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Description</h3>
        <p className="text-gray-700 dark:text-gray-300">{vacancy.description}</p>
      </div>
    )}

    {vacancy.requirements && (
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Requirements</h3>
        <p className="text-gray-700 dark:text-gray-300">{vacancy.requirements}</p>
      </div>
    )}

    {vacancy.documents && vacancy.documents.length > 0 && (
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Documents</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {vacancy.documents.map((document, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center space-x-3">
                <FileText className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{document.name}</p>
                  <p className="text-xs text-gray-500">{document.size}</p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button className="text-blue-500 hover:text-blue-700">
                  <Eye className="w-4 h-4" />
                </button>
                <button className="text-green-500 hover:text-green-700">
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

const ExamResultDetails = ({ result }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Basic Information</h3>
        <div className="space-y-3">
          <div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Exam Name:</span>
            <p className="text-gray-900 dark:text-white">{result.examName}</p>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Organization:</span>
            <p className="text-gray-900 dark:text-white">{result.organization}</p>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Exam Type:</span>
            <p className="text-gray-900 dark:text-white">{result.examType}</p>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Candidates:</span>
            <p className="text-gray-900 dark:text-white">{result.totalCandidates}</p>
          </div>
        </div>
      </div>
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Exam Details</h3>
        <div className="space-y-3">
          <div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Exam Date:</span>
            <p className="text-gray-900 dark:text-white">{formatDate(result.examDate)}</p>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Result Date:</span>
            <p className="text-gray-900 dark:text-white">{formatDate(result.resultDate)}</p>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Status:</span>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(result.status, 'result')}`}>
              {result.status}
            </span>
          </div>
        </div>
      </div>
    </div>

    {result.description && (
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Description</h3>
        <p className="text-gray-700 dark:text-gray-300">{result.description}</p>
      </div>
    )}

    {(result.resultLink || result.downloadLink) && (
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Links</h3>
        <div className="flex space-x-3">
          {result.resultLink && (
            <a
              href={result.resultLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Eye className="w-4 h-4 mr-2" />
              View Result
            </a>
          )}
          {result.downloadLink && (
            <a
              href={result.downloadLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </a>
          )}
        </div>
      </div>
    )}

    {result.documents && result.documents.length > 0 && (
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Documents</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {result.documents.map((document, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center space-x-3">
                <FileText className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{document.name}</p>
                  <p className="text-xs text-gray-500">{document.size}</p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button className="text-blue-500 hover:text-blue-700">
                  <Eye className="w-4 h-4" />
                </button>
                <button className="text-green-500 hover:text-green-700">
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

const NotificationDetails = ({ notification }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Basic Information</h3>
        <div className="space-y-3">
          <div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Title:</span>
            <p className="text-gray-900 dark:text-white">{notification.title}</p>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Type:</span>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(notification.type)}`}>
              {notification.type}
            </span>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Organization:</span>
            <p className="text-gray-900 dark:text-white">{notification.organization}</p>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Exam Name:</span>
            <p className="text-gray-900 dark:text-white">{notification.examName}</p>
          </div>
        </div>
      </div>
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Notification Details</h3>
        <div className="space-y-3">
          <div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Notification Date:</span>
            <p className="text-gray-900 dark:text-white">{formatDate(notification.notificationDate)}</p>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Priority:</span>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
              notification.priority === 'urgent' ? 'bg-red-100 text-red-800' :
              notification.priority === 'high' ? 'bg-orange-100 text-orange-800' :
              notification.priority === 'normal' ? 'bg-blue-100 text-blue-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {notification.priority}
            </span>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Target Audience:</span>
            <p className="text-gray-900 dark:text-white">{notification.targetAudience}</p>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Status:</span>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(notification.status, 'notification')}`}>
              {notification.status}
            </span>
          </div>
        </div>
      </div>
    </div>

    {notification.message && (
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Message</h3>
        <p className="text-gray-700 dark:text-gray-300">{notification.message}</p>
      </div>
    )}

    {notification.link && (
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Link</h3>
        <a
          href={notification.link}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <ExternalLink className="w-4 h-4 mr-2" />
          View Details
        </a>
      </div>
    )}

    {notification.documents && notification.documents.length > 0 && (
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Documents</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {notification.documents.map((document, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center space-x-3">
                <FileText className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{document.name}</p>
                  <p className="text-xs text-gray-500">{document.size}</p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button className="text-blue-500 hover:text-blue-700">
                  <Eye className="w-4 h-4" />
                </button>
                <button className="text-green-500 hover:text-green-700">
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

export default VacancyManagement;
